import { useCallback, useContext, useRef, useEffect } from 'react'
import { HeaderNav, TypeEditableTitle, Status } from '@components/layout/headerNav'
import { TitleWithBadge } from '@components/titleWithBadge'
import { submissionContext } from '@contexts'
import { useFetchApprovalRequests } from '@global/hooks'
import { useApprovalsEnabled } from '@global/hooks/useApprovalsEnabled'
import { useTemplate } from '@global/hooks/useTemplate'
import { t } from '@lingui/core/macro'
import { useSelector } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { getIsMetaIntegration } from '@utils/isMetaIntegration'

interface Props {
  templateId: string
  title?: string | null
  status?: Status | null
  editableTitleCallback?: TypeEditableTitle | null
  editableTitleRequired?: boolean
  editableDefaultTitle?: string
  titleValidationError?: boolean
  onBlurCallback?: (value: string) => void
  onFocusCallback?: () => void
  children?: React.ReactNode
}

const emptyFn = () => {
  // do nothing
}

/**
 * The header and navigation component for Datasets.
 * @param {string} templateId The template ID; used to determine the base path for routes.
 * @param {string} title The title of the page header.
 * @param {Status} status The status pill shown in the header; add rows, update rows, delete rows.
 * @param {TypeEditableTitle} editableTitleCallback The function called while editing inline the title.
 * @param {boolean} editableTitleRequired If a title is required; used by submissions.
 * @returns {ReactNode} HeaderNav component.
 */
export const DatasetsHeader = ({
  title = null,
  status = null,
  templateId,
  editableTitleCallback = null,
  editableTitleRequired = false,
  editableDefaultTitle = '',
  titleValidationError = false,
  onFocusCallback = emptyFn,
  onBlurCallback = emptyFn,
  children,
}: Props) => {
  const hasSubmissionsProcessingRef = useRef(false)
  const { template } = useTemplate({ templateId })
  const { fetchSubmissionsProcessingCheck } = useContext(submissionContext)

  const hasSubmissionsProcessing = hasSubmissionsProcessingRef.current
  const isApprovalsEnabled = useApprovalsEnabled()
  const BASE_PATH = `/template/${templateId}`
  const SHOW_APPROVAL_REQUESTS = isApprovalsEnabled && Boolean(template?.settings.require_approval)

  // Only Call this on the initial render.
  useEffect(() => {
    hasSubmissionsProcessingRef.current = fetchSubmissionsProcessingCheck(templateId)
  }, [])

  const isSelected = useCallback(
    function isSelected(name) {
      const currPathParts = location.pathname.split('/').filter(c => c)
      const currentPageName = currPathParts[currPathParts.length - 1]

      return currentPageName === name
    },
    [location.pathname],
  )

  const { approvalRequestsLoading, approvalRequestsCount } = useFetchApprovalRequests({
    showApprovalRequests: SHOW_APPROVAL_REQUESTS,
    template,
  })

  const [CAMPAIGN_HEALTH_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.CAMPAIGN_HEALTH]),
  )
  const isMetaIntegration = template && getIsMetaIntegration(template)

  const headerLinks = [
    { label: t`Data`, path: `${BASE_PATH}/data`, selected: isSelected('data') },
    {
      label: t`Submissions`,
      path: `${BASE_PATH}/submissions`,
      selected: isSelected('submissions'),
      badge: () => (
        <TitleWithBadge
          isLoading={hasSubmissionsProcessing}
          title={t`Submissions`}
          tooltip={hasSubmissionsProcessing ? t`Rows Processing` : ''}
        />
      ),
    },
    {
      label: t`Integrations`,
      path: `${BASE_PATH}/integrations`,
      selected: isSelected('integrations'),
    },
    { label: t`Pending`, path: `${BASE_PATH}/pending`, selected: isSelected('pending') },
    {
      label: t`Approval Requests`,
      path: `${BASE_PATH}/approval`,
      selected: isSelected('approval'),
      visible: SHOW_APPROVAL_REQUESTS,
      badge: () => (
        <TitleWithBadge
          isLoading={approvalRequestsLoading}
          count={approvalRequestsCount}
          title={t`Approval requests`}
        />
      ),
    },
    {
      label: t`Campaign health`,
      path: `${BASE_PATH}/campaign-health`,
      selected: isSelected('campaign-health'),
      visible: isMetaIntegration && CAMPAIGN_HEALTH_ENABLED,
    },
  ]

  const isSelectivePath = Boolean(location.pathname.includes('selective'))
  const isCreatePath = Boolean(location.pathname.includes('create'))
  const isDraftsPath = Boolean(location.pathname.includes('update'))
  const isResubmitPath = Boolean(location.pathname.includes('resubmit'))
  const hideHeaderLinks = isSelectivePath || isCreatePath || isDraftsPath || isResubmitPath

  return (
    <HeaderNav
      title={title}
      status={isSelectivePath ? Status.UPDATE_ROWS : status}
      links={hideHeaderLinks ? [] : headerLinks}
      editableTitleCallback={editableTitleCallback}
      editableTitleRequired={editableTitleRequired}
      editableDefaultTitle={editableDefaultTitle}
      titleValidationError={titleValidationError}
      onFocusCallback={onFocusCallback}
      onBlurCallback={onBlurCallback}
    >
      {children}
    </HeaderNav>
  )
}
